import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import LandingStyleHeader from '../components/LandingStyleHeader';
import { generateCaseStudy } from '../services/openaiService';

export default function CaseStudyScreen({ navigation }) {
  const { colors, glass, isDark, gradients } = useTheme();
  const [step, setStep] = useState(1); // 1: input, 2: result
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [technologies, setTechnologies] = useState('');
  const [keyFeatures, setKeyFeatures] = useState('');
  const [outcomes, setOutcomes] = useState('');
  const [link, setLink] = useState('');
  const [generatedCaseStudy, setGeneratedCaseStudy] = useState('');
  const [editableCaseStudy, setEditableCaseStudy] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    document.title = 'Flomodo - Case Study Creator';
  }, []);

  const handleGenerate = async () => {
    if (!projectName || !projectDescription) {
      alert('Please enter at least a project name and description');
      return;
    }

    setLoading(true);
    try {
      const caseStudy = await generateCaseStudy({
        projectName,
        projectDescription,
        technologies,
        keyFeatures,
        outcomes,
        link,
      });
      setGeneratedCaseStudy(caseStudy);
      setEditableCaseStudy(caseStudy);
      setStep(2);
    } catch (error) {
      alert('Error generating case study: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setProjectName('');
    setProjectDescription('');
    setTechnologies('');
    setKeyFeatures('');
    setOutcomes('');
    setLink('');
    setGeneratedCaseStudy('');
    setEditableCaseStudy('');
    setIsEditing(false);
  };

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(editableCaseStudy);
      alert('Case study copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
        <LandingStyleHeader navigation={navigation} currentScreen="casestudy" />
        <View style={[styles.loadingContainer, { backgroundColor: colors.primary }]}>
          <ActivityIndicator size="large" color={colors.accent} />
          <Text style={[styles.loadingText, { color: colors.text }]}>Crafting your case study...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundImage: gradients.background }]}>
      <LandingStyleHeader navigation={navigation} currentScreen="casestudy" />
      {step > 1 && (
        <TouchableOpacity onPress={handleReset} style={[styles.resetButton, { backgroundColor: colors.secondary, borderColor: colors.border, borderWidth: 1 }]}>
          <Text style={[styles.resetButtonText, { color: colors.text }]}>New Case Study</Text>
        </TouchableOpacity>
      )}

      <View style={styles.contentContainer}>
        {/* STEP 1: Input Details */}
        {step === 1 && (
          <View style={styles.stepContainer}>
            <Text style={[styles.stepTitle, { color: colors.text }]}>Create a Case Study</Text>
            <Text style={[styles.stepDescription, { color: colors.textSecondary }]}>
              Share project details and we'll generate a professional case study
            </Text>

            <View style={styles.inputGroup}>
              <Text style={[styles.inputLabel, { color: colors.text }]}>Project Name *</Text>
              <TextInput
                style={[styles.input, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
                placeholder="e.g., SpendHer - Creator Monetization App"
                placeholderTextColor={colors.textTertiary}
                value={projectName}
                onChangeText={setProjectName}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={[styles.inputLabel, { color: colors.text }]}>Project Description *</Text>
              <TextInput
                style={[styles.input, styles.textArea, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
                placeholder="Describe what you built, the problem it solves, and your role in the project..."
                placeholderTextColor={colors.textTertiary}
                value={projectDescription}
                onChangeText={setProjectDescription}
                multiline
                numberOfLines={6}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={[styles.inputLabel, { color: colors.text }]}>Technologies Used</Text>
              <TextInput
                style={[styles.input, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
                placeholder="e.g., React Native, Firebase, Stripe, Node.js"
                placeholderTextColor={colors.textTertiary}
                value={technologies}
                onChangeText={setTechnologies}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={[styles.inputLabel, { color: colors.text }]}>Key Features</Text>
              <TextInput
                style={[styles.input, styles.textArea, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
                placeholder="List the standout features (separate with commas or line breaks)"
                placeholderTextColor={colors.textTertiary}
                value={keyFeatures}
                onChangeText={setKeyFeatures}
                multiline
                numberOfLines={4}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={[styles.inputLabel, { color: colors.text }]}>Results & Outcomes</Text>
              <TextInput
                style={[styles.input, styles.textArea, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
                placeholder="What were the measurable results? (e.g., user growth, revenue, performance improvements)"
                placeholderTextColor={colors.textTertiary}
                value={outcomes}
                onChangeText={setOutcomes}
                multiline
                numberOfLines={4}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={[styles.inputLabel, { color: colors.text }]}>Project Link</Text>
              <TextInput
                style={[styles.input, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
                placeholder="https://yourproject.com"
                placeholderTextColor={colors.textTertiary}
                value={link}
                onChangeText={setLink}
                autoCapitalize="none"
                keyboardType="url"
              />
            </View>

            <TouchableOpacity
              style={styles.generateButton}
              onPress={handleGenerate}
              disabled={!projectName || !projectDescription}
            >
              <Text style={[styles.generateButtonText, { color: '#fff' }]}>Generate Case Study</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* STEP 2: Result */}
        {step === 2 && (
          <View style={styles.stepContainer}>
            <Text style={[styles.stepTitle, { color: colors.text }]}>Your Case Study</Text>
            <Text style={[styles.stepDescription, { color: colors.textSecondary }]}>
              Review and edit as needed
            </Text>

            <View style={[styles.resultCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
              {isEditing ? (
                <TextInput
                  style={[styles.editableText, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                  value={editableCaseStudy}
                  onChangeText={setEditableCaseStudy}
                  multiline
                />
              ) : (
                <Text style={[styles.resultText, { color: colors.text }]}>{editableCaseStudy}</Text>
              )}
            </View>

            <View style={styles.actionButtons}>
              <TouchableOpacity
                style={[styles.actionButton, styles.editButton, { backgroundColor: colors.secondary, borderColor: colors.border, borderWidth: 1 }]}
                onPress={() => setIsEditing(!isEditing)}
              >
                <Text style={[styles.actionButtonText, { color: colors.text }]}>
                  {isEditing ? 'Done Editing' : 'Edit'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionButton, styles.copyButton, { backgroundColor: colors.accent }]}
                onPress={handleCopy}
              >
                <Text style={[styles.actionButtonText, { color: '#fff' }]}>Copy</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    width: '100%',
    backgroundColor: '#ffffff',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    color: '#1e293b',
    fontSize: 16,
  },
  resetButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#ffffff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  resetButtonText: {
    color: '#1e293b',
    fontSize: 14,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingTop: 120,
    paddingBottom: 60,
    width: '100%',
  },
  stepContainer: {
    marginBottom: 30,
  },
  stepTitle: {
    color: '#1e293b',
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 8,
  },
  stepDescription: {
    color: '#64748b',
    fontSize: 15,
    marginBottom: 32,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    color: '#1e293b',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    fontSize: 15,
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  generateButton: {
    backgroundColor: '#4a9eff',
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: 'center',
    marginTop: 16,
  },
  generateButtonText: {
    color: '#1e293b',
    fontSize: 16,
    fontWeight: '600',
  },
  resultCard: {
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
  },
  resultText: {
    color: '#1e293b',
    fontSize: 15,
    lineHeight: 24,
  },
  editableText: {
    color: '#1e293b',
    fontSize: 15,
    lineHeight: 24,
    minHeight: 400,
    textAlignVertical: 'top',
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
  },
  editButton: {
    backgroundColor: '#ffffff',
    borderColor: '#e2e8f0',
  },
  copyButton: {
    backgroundColor: '#4a9eff',
    borderColor: '#4a9eff',
  },
  actionButtonText: {
    color: '#1e293b',
    fontSize: 15,
    fontWeight: '600',
  },
});

